import { CustomTemplate } from "./customTemplateTypes";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);

    img.onerror = () =>
      reject(
        new Error(
          `Bild konnte nicht geladen werden: ${src}`
        )
      );

    img.src = src;
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(
          new Error(
            "Bilddatei konnte nicht gelesen werden."
          )
        );
      }
    };

    reader.onerror = () =>
      reject(
        new Error(
          "Bilddatei konnte nicht gelesen werden."
        )
      );

    reader.readAsDataURL(file);
  });
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const imageRatio =
    image.width / image.height;

  const areaRatio =
    width / height;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = image.width;
  let sourceHeight = image.height;

  if (imageRatio > areaRatio) {
    sourceWidth =
      image.height * areaRatio;

    sourceX =
      (image.width - sourceWidth) / 2;
  } else {
    sourceHeight =
      image.width / areaRatio;

    sourceY =
      (image.height - sourceHeight) / 2;
  }

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height
  );
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number,
  color: string,
  textAlign: "left" | "center" | "right",
  fontFamily: string,
) {
  const padding = 10;

  const usableWidth =
    Math.max(1, width - padding * 2);

  const usableHeight =
    Math.max(1, height - padding * 2);

  let currentFontSize = fontSize;

  const words = text
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return;
  }

  while (currentFontSize >= 12) {
    ctx.font =
  `${currentFontSize}px "${fontFamily}"`;

    const lineHeight =
      currentFontSize * 1.2;

    const lines: string[] = [];

    let currentLine = "";

    for (const word of words) {
      const testLine =
        currentLine
          ? `${currentLine} ${word}`
          : word;

      if (
        ctx.measureText(testLine).width >
          usableWidth &&
        currentLine
      ) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    if (
      lines.length * lineHeight <=
      usableHeight
    ) {
      ctx.fillStyle = color;
      ctx.textBaseline = "top";
      ctx.textAlign = textAlign;

      let textX = x + padding;

      if (textAlign === "center") {
        textX =
          x + width / 2;
      }

      if (textAlign === "right") {
        textX =
          x + width - padding;
      }

      lines.forEach(
        (line, index) => {
          ctx.fillText(
            line,
            textX,
            y +
              padding +
              index * lineHeight
          );
        }
      );

      return;
    }

    currentFontSize -= 2;
  }
}

export async function generateCustomTemplateImage(
  originalFile: File | null,
  text: string,
  template: CustomTemplate
): Promise<string> {
  const canvas =
    document.createElement("canvas");

  canvas.width = template.width;
  canvas.height = template.height;

  const ctx =
    canvas.getContext("2d");

  if (!ctx) {
    throw new Error(
      "Canvas konnte nicht initialisiert werden."
    );
  }

  // Eingabebild laden
  let inputImage:
    HTMLImageElement | null = null;

  if (
    originalFile &&
    originalFile.type.startsWith("image/")
  ) {
    const imageDataUrl =
      await fileToDataUrl(originalFile);

    inputImage =
      await loadImage(imageDataUrl);
  }

  // ----------------------------------
  // 1. BILDFELDER UNTEN ZEICHNEN
  // ----------------------------------

  if (inputImage) {
    for (const field of template.fields) {
      if (field.type !== "image") {
        continue;
      }

      const x =
        field.x * canvas.width;

      const y =
        field.y * canvas.height;

      const width =
        field.width * canvas.width;

      const height =
        field.height * canvas.height;

      drawImageCover(
        ctx,
        inputImage,
        x,
        y,
        width,
        height
      );
    }
  }

  // ----------------------------------
  // 2. TEMPLATE DARÜBER ZEICHNEN
  // ----------------------------------

  const templateImage =
    await loadImage(
      template.backgroundImageUrl
    );

  ctx.drawImage(
    templateImage,
    0,
    0,
    canvas.width,
    canvas.height
  );

  // ----------------------------------
  // 3. TEXT GANZ OBEN ZEICHNEN
  // ----------------------------------

  for (const field of template.fields) {
    if (field.type !== "text") {
      continue;
    }

    const x =
      field.x * canvas.width;

    const y =
      field.y * canvas.height;

    const width =
      field.width * canvas.width;

    const height =
      field.height * canvas.height;

    const fieldText =
    field.text?.trim()
        ? field.text
        : text;

    drawWrappedText(
    ctx,
    text,
    x,
    y,
    width,
    height,
    field.fontSize ?? 32,
    field.color ?? "#000000",
    field.textAlign ?? "left",
    field.fontFamily ?? "Barlow",
    );
  }

  return canvas.toDataURL(
    "image/png"
  );
}